import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  AssistantAliasDto,
  AssistantAliasSource,
  AssistantAliasStatus,
  AssistantLinkDto,
  AssistantLinkedPhoneDto,
  AssistantNoteDto,
  AssistantOutcome,
  AssistantSettingsDto,
  AssistantStatusDto,
  AssistantTurnDto,
  BoxDetailDto,
  BoxState,
  CountDto,
  CountHiddenDto,
  CountListItemDto,
  CountProgressDto,
  CountingSkusDto,
  CountResultDto,
  DeviceDto,
  DiscrepancyDto,
  GoogleSheetDto,
  LabelBatchDto,
  MeDto,
  OrderDetailDto,
  OrderDto,
  OrderStatus,
  OrgDto,
  PlannedQuantityDto,
  SiteDto,
  SkuDto,
  StockBoxDto,
  StockRowDto,
  SuggestedCountDto,
  TransferDetailDto,
  TransferDto,
  TransferStatus,
  UserDto,
  WhatsAppDto,
  WhatsAppGroupDto,
} from '@pl/domain';
import { useMemo } from 'react';
import { ApiError, api } from './api';

export interface TransfersQuery {
  siteId?: string;
  direction?: 'outgoing' | 'incoming';
  status?: TransferStatus[];
  limit?: number;
}

export interface CountsQuery {
  siteId?: string;
  /** Counts started at or after this instant (ISO, with offset). */
  since?: string;
  /** Counts finished at or after this instant (ISO, with offset), whenever they started. */
  finishedSince?: string;
  status?: 'open' | 'finished' | 'cancelled';
  limit?: number;
  /** An open count's running tally. Only the office asks: on a phone it gives another count's expected. */
  include?: 'scanned';
}

export interface DiscrepanciesQuery {
  siteId?: string;
  status?: 'open' | 'resolved';
  /** One count's or one transfer's discrepancies. */
  sourceId?: string;
  limit?: number;
}
/** The turn log's filters (Task 12). `day` is read in `tz`, the company's clock. */
export interface TurnFilters {
  userId?: string;
  outcome?: AssistantOutcome;
  day?: string;
  tz: string;
}

/** Turns per page of Office → Assistant's conversations. A full page means there may be older ones. */
export const TURN_PAGE = 50;

export interface OrdersQuery {
  siteId?: string;
  status?: OrderStatus[];
  search?: string;
  limit?: number;
}

const qs = (o: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const qk = {
  me: ['me'] as const,
  org: ['org'] as const,
  assistantLinks: ['assistant-links'] as const,
  assistantStatus: ['assistant-status'] as const,
  assistantNotes: ['assistant-notes'] as const,
  assistantAliases: (status: string, source?: string) => ['assistant-aliases', status, source ?? ''] as const,
  assistantTurns: (f: TurnFilters) => ['assistant-turns', f] as const,
  assistantSettings: ['assistant-settings'] as const,
  testData: ['test-data'] as const,
  assistantLinkedPhones: ['assistant-linked-phones'] as const,
  sites: ['sites'] as const,
  /** Every SKU list, whatever its search or inactive filter — for invalidating after an edit or import. */
  allSkus: ['skus'] as const,
  skus: (search = '', includeInactive = false) => ['skus', search, includeInactive] as const,
  stock: (siteId = 'all') => ['stock', siteId] as const,
  stockBoxes: (siteId: string, skuId: string, state = '') => ['stock-boxes', siteId, skuId, state] as const,
  availability: (skuIds: string[]) => ['availability', [...skuIds].sort().join(',')] as const,
  orders: (q: OrdersQuery) => ['orders', q] as const,
  order: (id: string) => ['order', id] as const,
  box: (code: string) => ['box', code] as const,
  batches: ['label-batches'] as const,
  planned: (siteId: string, skuId: string) => ['planned-quantities', siteId, skuId] as const,
  users: ['users'] as const,
  devices: ['devices'] as const,
  googleSheet: ['google-sheet'] as const,
  whatsapp: ['whatsapp'] as const,
  whatsappGroups: ['whatsapp', 'groups'] as const,
  /** Not under `whatsapp`: nothing that refreshes the page's own queries may bring the list back. */
  whatsappGroupChooser: ['whatsapp-group-chooser'] as const,
  transfers: (q: TransfersQuery) => ['transfers', q] as const,
  transfer: (id: string) => ['transfer', id] as const,
  suggestedCounts: (siteId: string) => ['counts-suggested', siteId] as const,
  // Not under 'counts': the open-count lists are patched in place when the phone goes into a count.
  countingSkus: (siteId: string) => ['counts-counting', siteId] as const,
  counts: (q: CountsQuery) => ['counts', q] as const,
  // Not under 'counts': invalidating the lists must never reach a count that is being scanned.
  count: (id: string) => ['count', id] as const,
  // Its own key, not under 'count': the count screen's read never shares the full count's cache.
  countProgress: (id: string) => ['count-progress', id] as const,
  discrepancies: (q: DiscrepanciesQuery) => ['discrepancies', q] as const,
};

const ME_KEY = 'pl.me';

/**
 * The last identity the server confirmed on this phone. A worker at a truck with no signal opens
 * an app that cannot ask who they are; without this the whole scan surface is replaced by an
 * error and they cannot scan at all. The server stays the only authority — every scan is
 * authorised by the session cookie — so a stale copy can only decide which screens render.
 */
export function cachedMe(): MeDto | null {
  try {
    const raw = localStorage.getItem(ME_KEY);
    return raw ? (JSON.parse(raw) as MeDto) : null;
  } catch {
    return null; // private mode, or a value someone else wrote
  }
}

export function rememberMe(me: MeDto): MeDto {
  try {
    localStorage.setItem(ME_KEY, JSON.stringify(me));
  } catch {
    /* the phone just cannot boot offline */
  }
  return me;
}

export function forgetMe(): void {
  try {
    localStorage.removeItem(ME_KEY);
  } catch {
    /* nothing to forget with */
  }
}

/** A 401 is a decision about the session; anything else says only that the server was not reached. */
export const isOfflineError = (error: unknown): boolean => !(error instanceof ApiError) || error.status >= 500;

export const useMe = () =>
  useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return rememberMe(await api.get<MeDto>('/auth/me'));
      } catch (err) {
        // Only the server saying "no session" clears the cached identity. A dead network must not,
        // or the first boot without signal would throw away the thing that makes it work.
        if (err instanceof ApiError && err.status === 401) forgetMe();
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
    initialData: () => cachedMe() ?? undefined,
    // Stale from the first render, so the cached identity is shown *and* the server is still asked.
    initialDataUpdatedAt: 0,
  });
export const useOrg = () => useQuery({ queryKey: qk.org, queryFn: () => api.get<OrgDto>('/org'), staleTime: 60_000 });
export const useSites = () => useQuery({ queryKey: qk.sites, queryFn: () => api.get<SiteDto[]>('/sites'), staleTime: 5 * 60_000 });
export const useSkus = (opts: { search?: string; includeInactive?: boolean } = {}) =>
  useQuery({
    queryKey: qk.skus(opts.search, opts.includeInactive),
    queryFn: () => api.get<SkuDto[]>(`/skus${qs({ search: opts.search, includeInactive: opts.includeInactive ? 'true' : undefined })}`),
    staleTime: 60_000,
  });
export function useSkuMap(): Map<string, SkuDto> {
  const { data } = useSkus({ includeInactive: true });
  return useMemo(() => new Map((data ?? []).map((s) => [s.id, s])), [data]);
}
export function useSiteMap(): Map<string, SiteDto> {
  const { data } = useSites();
  return useMemo(() => new Map((data ?? []).map((s) => [s.id, s])), [data]);
}
export const useStock = (siteId?: string) =>
  useQuery({ queryKey: qk.stock(siteId), queryFn: () => api.get<StockRowDto[]>(`/stock${qs({ siteId })}`), refetchInterval: 20_000 });
export const useStockBoxes = (siteId: string | null, skuId: string | null, state?: BoxState) =>
  useQuery({
    queryKey: qk.stockBoxes(siteId ?? '', skuId ?? '', state),
    queryFn: () => api.get<StockBoxDto[]>(`/stock/boxes${qs({ siteId: siteId!, skuId: skuId!, state })}`),
    enabled: Boolean(siteId && skuId),
  });
export const useAvailability = (skuIds: string[]) =>
  useQuery({
    queryKey: qk.availability(skuIds),
    queryFn: () => api.get<StockRowDto[]>(`/availability${qs({ skuIds: skuIds.join(',') })}`),
    enabled: skuIds.length > 0,
  });
/** `enabled` is a second argument rather than part of `q`, so it cannot leak into the query key. */
export const useOrders = (q: OrdersQuery, enabled = true) =>
  useQuery({
    queryKey: qk.orders(q),
    queryFn: () => api.get<OrderDto[]>(`/orders${qs({ siteId: q.siteId, status: q.status?.join(','), search: q.search, limit: q.limit })}`),
    refetchInterval: 15_000,
    enabled,
  });
export const useOrder = (id: string | undefined) =>
  useQuery({ queryKey: qk.order(id ?? ''), queryFn: () => api.get<OrderDetailDto>(`/orders/${id}`), enabled: Boolean(id) });
export const useBox = (code: string | undefined) =>
  useQuery({ queryKey: qk.box(code ?? ''), queryFn: () => api.get<BoxDetailDto>(`/boxes/${encodeURIComponent(code!)}`), enabled: Boolean(code), retry: false });
/** Settings → Users. Pass `enabled: false` for anyone without `settings`: the API would only say 403. */
export const useUsers = (enabled = true) => useQuery({ queryKey: qk.users, queryFn: () => api.get<UserDto[]>('/users'), enabled });
/** Settings → Devices. Refetched every 15 s, so a TV paired a moment ago soon reads "Just now", not "Never". */
export const useDevices = (enabled = true) =>
  useQuery({ queryKey: qk.devices, queryFn: () => api.get<DeviceDto[]>('/devices'), enabled, refetchInterval: 15_000 });
/** Settings → Google Sheets. Polled, so a write the jobs make shows up while the page is open. */
export const useGoogleSheet = (enabled = true) =>
  useQuery({ queryKey: qk.googleSheet, queryFn: () => api.get<GoogleSheetDto>('/integrations/google-sheet'), retry: false, refetchInterval: 30_000, enabled });
/**
 * Settings → WhatsApp. Every 3 s while pairing, because the QR changes about every 20 s and a scan
 * needs the current one; every 15 s otherwise, so a phone that unlinks, or an alert, shows up.
 */
export const useWhatsApp = (enabled = true) =>
  useQuery({
    queryKey: qk.whatsapp,
    queryFn: () => api.get<WhatsAppDto>('/whatsapp'),
    retry: false,
    refetchInterval: (q) => (q.state.data?.state === 'pairing' ? 3_000 : 15_000),
    enabled,
  });
/**
 * The chosen alert group, alone, with its member count: the API names no other group but to the
 * chooser. Asked again a minute after a failure, as its message says ("Try again in a minute").
 */
export const useWhatsAppGroups = (enabled: boolean) =>
  useQuery({
    queryKey: qk.whatsappGroups,
    queryFn: () => api.get<WhatsAppGroupDto[]>('/whatsapp/groups'),
    retry: false,
    refetchInterval: (q) => (q.state.status === 'error' ? 60_000 : false),
    enabled,
  });
/**
 * Every group the WhatsApp number is in, for the chooser alone (`?choose=1`): the number's groups are
 * its owner's business, so the list is asked for only while the chooser is open, and never kept once
 * it closes (`gcTime` 0; the page drops it too).
 */
export const useWhatsAppGroupChooser = (enabled: boolean) =>
  useQuery({
    queryKey: qk.whatsappGroupChooser,
    queryFn: () => api.get<WhatsAppGroupDto[]>('/whatsapp/groups?choose=1'),
    retry: false,
    gcTime: 0,
    refetchInterval: (q) => (q.state.status === 'error' ? 60_000 : false),
    enabled,
  });
export const useBatches = () => useQuery({ queryKey: qk.batches, queryFn: () => api.get<LabelBatchDto[]>('/label-batches?limit=30') });
/** What the import planned for one SKU at one site; an empty list when it planned nothing there. */
export const usePlannedQuantity = (siteId: string | null, skuId: string | null) =>
  useQuery({
    queryKey: qk.planned(siteId ?? '', skuId ?? ''),
    queryFn: () => api.get<PlannedQuantityDto[]>(`/planned-quantities${qs({ siteId: siteId!, skuId: skuId! })}`),
    enabled: Boolean(siteId && skuId),
    staleTime: 60_000,
  });
/** Refreshed every 15 seconds, so a transfer sent from another site turns up here without a pull. */
export const useTransfers = (q: TransfersQuery, enabled = true) =>
  useQuery({
    queryKey: qk.transfers(q),
    queryFn: () => api.get<TransferDto[]>(`/transfers${qs({ siteId: q.siteId, direction: q.direction, status: q.status?.join(','), limit: q.limit })}`),
    refetchInterval: 15_000,
    enabled,
  });
export const useTransfer = (id: string | undefined) =>
  useQuery({ queryKey: qk.transfer(id ?? ''), queryFn: () => api.get<TransferDetailDto>(`/transfers/${id}`), enabled: Boolean(id) });
/**
 * Today's suggestions, in the server's order. Only `skuId` and `lastCountedAt` are ever read: the
 * DTO has carried `inStock`, which is the answer to the count it suggests.
 */
export const useSuggestedCounts = (siteId: string | undefined) =>
  useQuery({
    queryKey: qk.suggestedCounts(siteId ?? ''),
    queryFn: async () =>
      (await api.get<SuggestedCountDto[]>(`/counts/suggested${qs({ siteId })}`)).map((row) => ({ skuId: row.skuId, lastCountedAt: row.lastCountedAt })),
    enabled: Boolean(siteId),
  });
/**
 * The SKUs under an open count at the site, whose box codes the Ship screen hides (`hiddenSkus`).
 * Only `skuIds` is kept. Live signals keep it fresh; the interval is a safety net, as for the lists.
 */
export const useCountingSkus = (siteId: string | undefined) =>
  useQuery({
    queryKey: qk.countingSkus(siteId ?? ''),
    queryFn: async (): Promise<CountingSkusDto> => ({ skuIds: (await api.get<CountingSkusDto>(`/counts/counting${qs({ siteId })}`)).skuIds }),
    refetchInterval: 30_000,
    enabled: Boolean(siteId),
  });
/**
 * Newest first. List items carry no expected or found (the server leaves them out), only what is
 * left for a lead — but even that is never shown on the phone for a SKU or pallet being counted.
 */
/** `refetchMs`: how often to ask again; the phone's result screens ask for their site's open counts faster (`OPEN_POLL_MS`). */
export const useCounts = (q: CountsQuery, enabled = true, refetchMs = 30_000) =>
  useQuery({
    queryKey: qk.counts(q),
    queryFn: () => api.get<CountListItemDto[]>(`/counts${qs({ siteId: q.siteId, since: q.since, finishedSince: q.finishedSince, status: q.status, limit: q.limit, include: q.include })}`),
    refetchInterval: refetchMs,
    enabled,
  });
/**
 * What the scan app's count screen reads (`GET /counts/:id/progress`): what the count is of, whether it
 * is still open, and its tally — never a result, whatever its status. The full count (`useCount`) is
 * the result screen's alone, behind its gate.
 */
export const useCountProgress = (id: string | undefined) =>
  useQuery({ queryKey: qk.countProgress(id ?? ''), queryFn: () => api.get<CountProgressDto>(`/counts/${id}/progress`), enabled: Boolean(id) });
/**
 * The full count, as the office reads it: an open count answers with its tally only; a finished one
 * with its result and boxes. The phone's result screen asks its own question, once per visit and
 * stamped with when it asked (`CountResult`).
 */
export const useCount = (id: string | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.count(id ?? ''),
    queryFn: () => api.get<CountDto | CountResultDto | CountHiddenDto>(`/counts/${id}`),
    enabled: Boolean(id) && enabled,
  });
export const useDiscrepancies = (q: DiscrepanciesQuery, enabled = true) =>
  useQuery({
    queryKey: qk.discrepancies(q),
    queryFn: () => api.get<DiscrepancyDto[]>(`/discrepancies${qs({ siteId: q.siteId, status: q.status, sourceId: q.sourceId, limit: q.limit })}`),
    enabled,
  });

/** The caller's linked WhatsApp numbers. Polled while a link code is on screen, so a new link shows up by itself. */
export const useAssistantLinks = (refetchMs: number | false = false) =>
  useQuery({ queryKey: qk.assistantLinks, queryFn: () => api.get<AssistantLinkDto[]>('/assistant/links'), refetchInterval: refetchMs });

/* Office → Assistant (Task 12): admins only; nobody else's page asks for these. */
export const useAssistantStatus = () =>
  useQuery({
    queryKey: qk.assistantStatus,
    queryFn: () => api.get<AssistantStatusDto>('/assistant/status'),
    // Sooner while a rebuild is on its way, so the new build shows up about when it lands.
    refetchInterval: (q) => (q.state.data?.rebuildScheduled ? 5_000 : 30_000),
  });
export const useAssistantNotes = () => useQuery({ queryKey: qk.assistantNotes, queryFn: () => api.get<AssistantNoteDto[]>('/assistant/notes') });
export const useAssistantAliases = (status: AssistantAliasStatus, source?: AssistantAliasSource) =>
  useQuery({ queryKey: qk.assistantAliases(status, source), queryFn: () => api.get<AssistantAliasDto[]>(`/assistant/aliases${qs({ status, source })}`) });
/** Newest first, a page at a time: each older page starts before the last turn already shown (the API's cursor). */
export const useAssistantTurns = (f: TurnFilters) =>
  useInfiniteQuery({
    queryKey: qk.assistantTurns(f),
    queryFn: ({ pageParam }) =>
      api.get<AssistantTurnDto[]>(`/assistant/turns${qs({ limit: TURN_PAGE, before: pageParam, userId: f.userId, outcome: f.outcome, day: f.day, tz: f.day ? f.tz : undefined })}`),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => (last.length === TURN_PAGE ? last[last.length - 1]!.id : undefined),
    refetchInterval: 30_000,
  });
export const useAssistantSettings = () => useQuery({ queryKey: qk.assistantSettings, queryFn: () => api.get<AssistantSettingsDto>('/assistant/settings') });
/** Office → Assistant → Linked phones: every phone linked in the company (admins). */
export const useAssistantLinkedPhones = () => useQuery({ queryKey: qk.assistantLinkedPhones, queryFn: () => api.get<AssistantLinkedPhoneDto[]>('/assistant/linked-phones') });
